import { useRef, useState } from 'react';
import { ImagePlus, Loader2, Trash2, Star, ArrowLeft, ArrowRight } from 'lucide-react';
import * as api from '../../lib/api';
import { uploadUrl } from '../../lib/uploads';

const MAX_PHOTOS = 12;
const MAX_MB = 6;

/**
 * Manage the photos a customer sees.
 *
 * The first photo is the cover — it is the one on the search card, and the
 * search card is where someone decides whether to click at all. So "make
 * cover" is a first-class action rather than something implied by drag order:
 * dragging is fiddly on a phone, and this gets used from a phone, standing in
 * the yard, having just taken the picture.
 */
export default function FacilityPhotos({ facilityId, initial = [] }) {
  const inputRef = useRef(null);
  const [photos, setPhotos] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const paths = photos.map((p) => (typeof p === 'string' ? p : p.path));

  const run = async (fn) => {
    setBusy(true); setError(null);
    try {
      const result = await fn();
      setPhotos(result.photos);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const onPick = async (event) => {
    const files = [...(event.target.files ?? [])];
    // Reset immediately so picking the same file twice still fires onChange.
    event.target.value = '';
    if (files.length === 0) return;

    setBusy(true); setError(null);
    try {
      // Sequential, not Promise.all: each upload appends to the same array
      // column, so parallel writes would race and silently drop one.
      let latest = null;
      for (const file of files) {
        if (paths.length + files.indexOf(file) >= MAX_PHOTOS) break;
        latest = await api.uploadFacilityPhoto(facilityId, file);
      }
      if (latest) setPhotos(latest.photos);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const move = (index, delta) => {
    const next = [...paths];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    run(() => api.reorderFacilityPhotos(facilityId, next));
  };

  const makeCover = (index) => {
    if (index === 0) return;
    const next = [...paths];
    const [chosen] = next.splice(index, 1);
    run(() => api.reorderFacilityPhotos(facilityId, [chosen, ...next]));
  };

  const full = paths.length >= MAX_PHOTOS;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-bold text-slate-900">Photos</h2>
        <span className="text-xs text-slate-500">{paths.length} of {MAX_PHOTOS}</span>
      </div>
      <p className="mb-4 text-xs text-slate-500">
        The first photo is the cover — it is the one on the search results, so make it the
        one that shows the yard best. Up to {MAX_MB}MB each. Location data is stripped from
        every upload.
      </p>

      {error && <p role="alert" className="mb-3 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">{error}</p>}

      {paths.length > 0 && (
        <ul className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {paths.map((path, index) => (
            <li key={path} className="overflow-hidden rounded-xl border border-slate-200">
              <div className="relative">
                <img src={uploadUrl(path)} alt={`Facility photo ${index + 1}`}
                     className="h-36 w-full bg-slate-100 object-cover" loading="lazy" />
                {index === 0 && (
                  <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-slate-900/85 px-2 py-0.5 text-[11px] font-bold text-white">
                    <Star className="h-3 w-3 fill-current" aria-hidden="true" /> Cover
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between gap-1 p-2">
                <div className="flex gap-1">
                  <button type="button" disabled={busy || index === 0} onClick={() => move(index, -1)}
                          aria-label={`Move photo ${index + 1} earlier`}
                          className="grid h-7 w-7 place-items-center rounded-md border border-slate-300 text-brand-600 hover:bg-slate-50 disabled:opacity-30">
                    <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                  <button type="button" disabled={busy || index === paths.length - 1} onClick={() => move(index, 1)}
                          aria-label={`Move photo ${index + 1} later`}
                          className="grid h-7 w-7 place-items-center rounded-md border border-slate-300 text-brand-600 hover:bg-slate-50 disabled:opacity-30">
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                  {index !== 0 && (
                    <button type="button" disabled={busy} onClick={() => makeCover(index)}
                            className="rounded-md border border-slate-300 px-2 text-[12px] font-bold text-slate-700 hover:bg-slate-50">
                      Make cover
                    </button>
                  )}
                </div>
                <button type="button" disabled={busy}
                        aria-label={`Delete photo ${index + 1}`}
                        onClick={() => {
                          if (!window.confirm('Delete this photo? It cannot be recovered.')) return;
                          run(() => api.deleteFacilityPhoto(facilityId, path));
                        }}
                        className="grid h-7 w-7 place-items-center rounded-md text-rose-600 hover:bg-rose-50">
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif"
             multiple className="sr-only" onChange={onPick} />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={busy || full}
              className="inline-flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              : <ImagePlus className="h-4 w-4" aria-hidden="true" />}
        {full ? `Limit of ${MAX_PHOTOS} reached` : paths.length === 0 ? 'Add photos' : 'Add more'}
      </button>

      {paths.length === 0 && (
        <p className="mt-2 text-xs text-slate-500">
          A listing with no photo still appears in search, with a grey placeholder where the
          yard should be.
        </p>
      )}
    </section>
  );
}
